/**
 * Analytics consent banner — Phase 2 privacy gate.
 *
 * Renders exactly once, at the bottom of the page, when the visitor
 * has not yet expressed an analytics preference. Provides two
 * equally-prominent buttons (Accept Analytics / Reject Non-Essential)
 * and a link to the Privacy Policy.
 *
 * Behaviour:
 *   · Neither GA4 nor Clarity has loaded yet — `initAnalytics()`
 *     honours `deny by default`. Accepting flips consent, updates
 *     Google Consent Mode, and boots the providers on the current
 *     page. Rejecting persists a denial so nothing loads on future
 *     visits either.
 *   · A visitor can revisit their choice via the "Analytics
 *     Preferences" link (rendered elsewhere) which calls
 *     `resetAnalyticsConsent()` and this banner shows again.
 *   · The banner never blocks essential site behaviour and is fully
 *     keyboard accessible.
 */
import React, { useEffect, useState } from "react";
import {
  getConsentState, setAnalyticsConsent, resetAnalyticsConsent,
  ANALYTICS_CONSENT_KEY,
} from "@/lib/analytics";

export default function AnalyticsConsentBanner() {
  // Show ONLY when the visitor has never chosen. `resetAnalytics
  // Consent` (from the Analytics Preferences link) puts us back
  // into the `unset` state and re-mounts the banner.
  const [state, setState] = useState(() => getConsentState());

  // Cross-tab sync — if consent changes in another tab, reflect it
  // here so the banner disappears/re-appears in this tab too.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === ANALYTICS_CONSENT_KEY) setState(getConsentState());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (state !== "unset") return null;

  const decide = (choice) => {
    setAnalyticsConsent(choice);
    setState(choice);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 border-t-2 border-[var(--ink)] bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.12)]"
      role="dialog"
      aria-modal="false"
      aria-labelledby="analytics-consent-title"
      data-testid="analytics-consent-banner"
    >
      <div className="max-w-6xl mx-auto p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
        <div className="flex-1 min-w-0">
          <div id="analytics-consent-title" className="font-bold text-sm md:text-base">
            Help us improve CollectorUp?
          </div>
          <p className="text-xs md:text-sm text-[var(--smoke)] mt-1">
            We&apos;d like to measure page visits and record anonymous session
            interactions with Google Analytics and Microsoft Clarity so we
            know which parts of the site work best. We never intentionally
            send your name, email, phone, address, payment details, or order
            IDs to these services.{" "}
            <a
              href="/privacy" className="underline font-semibold"
              data-testid="analytics-consent-privacy-link"
            >
              Read the Privacy Policy
            </a>.
          </p>
        </div>
        <div className="flex flex-col-reverse md:flex-row gap-2 md:items-center shrink-0">
          <button
            type="button" onClick={() => decide("denied")}
            className="btn text-xs md:text-sm md:min-w-[180px]"
            data-testid="analytics-consent-reject"
          >
            Reject Non-Essential
          </button>
          <button
            type="button" onClick={() => decide("granted")}
            className="btn bg-[var(--panini-red)] text-white text-xs md:text-sm md:min-w-[180px]"
            data-testid="analytics-consent-accept"
          >
            Accept Analytics
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Small text link that lets visitors reopen the banner. Renders as a
 * plain button so it can sit in a footer without needing router
 * navigation. Clicking wipes the stored preference and forces the
 * banner to re-mount on the next render tick.
 */
export function AnalyticsPreferencesLink({ className = "" }) {
  const openBanner = () => {
    resetAnalyticsConsent();
    // Force a full re-render so the banner remounts. Custom event
    // is picked up by any mounted `AnalyticsConsentBanner`.
    window.dispatchEvent(new StorageEvent("storage",
      { key: ANALYTICS_CONSENT_KEY }));
    // Fallback if StorageEvent constructor is not supported.
    window.location.reload();
  };
  return (
    <button
      type="button" onClick={openBanner}
      className={`underline text-xs ${className}`}
      data-testid="analytics-preferences-link"
    >
      Analytics Preferences
    </button>
  );
}
